import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import ImageAndVideo from '@/src/components/Modules-revamp/ImageAndPoster/ImageAndVideo';
import ImageCarouselName from '@/src/components/Modules-revamp/ImageAndPoster/ImageCarouselName';
import Layers from '@/src/components/Modules-revamp/ImageAndPoster/Layers';
import { Field } from '@/src/components/RcForm';
import { useImageCarousel } from '@/src/constrains/SalesChannelContent-Revamp/hooks/useImageCarousel';
import {
  ModalField,
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import { get } from 'lodash';
import React from 'react';
import Animation from 'src/components/Modules-revamp/Animation';
import AutoPlay from 'src/components/Modules-revamp/AutoPlay';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import Type from 'src/components/Modules-revamp/CarouselType';
import Images from 'src/components/Modules-revamp/Images';
import ModuleWidth from 'src/components/Modules-revamp/ModuleWidthSelect';
import ResizingOptions from 'src/components/Modules-revamp/ResponsiveResizingOptionSelect';
import { ContentModuleGroupPreloadingContext } from 'src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import {
  checkResponsiveImageCarousel,
  validateCarouselSetting,
} from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import imageCarouseRenderTemplate from 'src/strategies/render/imageCarousel';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import moduleStyleRender from '../../ModuleStyle';
// import { EPreviewSize } from 'src/types/common/SalesChannel';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};

const imageCarouselRender = (form: any, breakpoint: any, extra: any) => {
  //const { onModalConfirm } = extra || {};
  const values = form.getFieldsValue(true);
  const { handleOnSectionReset, handleOnSectionCopy, salesChannelMode } = extra;
  const list = imageCarouseRenderTemplate(values, breakpoint, salesChannelMode);
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => imageCarouselComponent(f, breakpoint, extra));
    }
  };
  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const imageCarouselComponent = (
  data: ResponsiveFieldProps,
  breakpoint: any,
  extra: any,
) => {
  const {
    onModalConfirm,
    onAdd,
    onDelete,
    onSort,
    animations,
    hoverAnimations,
    buttons,
    form,
  } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.CarouselType: {
      return <Type disabled={data?.disabled} options={data?.options || []} />;
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.AutoPlay: {
      return <AutoPlay {...data} />;
    }
    case ResponsiveField.ResizingOption: {
      return (
        <ResizingOptions
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.Images: {
      const { checkImage, checkImageLink, checkName } =
        validateCarouselSetting();
      const { checkImage: checkCarouselImage } = checkResponsiveImageCarousel();
      return (
        <Images
          validators={{
            name: checkName,
            image: checkImage,
            link: checkImageLink,
          }}
          allValidator={checkCarouselImage}
          moduleType={EnumModuleType.ResponsiveImageCarousel}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
          buttons={formButtonList(buttons || [])}
          draggableId={draggableId}
          breakpoint={breakpoint}
          disabled={data?.disabled}
          hasModal={data?.hasModal}
          modalFunc={data?.modalFunc}
          onModalConfirm={onModalConfirm}
          onAdd={onAdd}
          onDelete={onDelete}
          onSort={onSort}
        />
      );
    }
    default:
      return;
  }
};

export const imageCarouselModal = (data: any, extra) => {
  const { validators, breakpoint, editForm, layerForm, selectedLayer } =
    extra || {};
  switch (data?.field) {
    case ModalField.Name: {
      return (
        <ImageCarouselName
          validator={{ name: get(validators, 'name') }}
          breakpoint={breakpoint}
        />
      );
    }
    case ModalField.Image: {
      return (
        <ImageAndVideo
          validators={{
            image: get(validators, 'image'),
            link: get(validators, 'link'),
          }}
          breakpoint={breakpoint}
        />
      );
    }
    case ModalField.Layer: {
      return (
        <Layers
          onClick={extra?.onClick}
          onAdd={extra?.onAdd}
          onDelete={extra?.onDelete}
          onReorder={extra?.onReorder}
          extra={{ editForm, layerForm, selectedLayer, breakpoint }}
        />
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }: any) => {
  const { handleOnModalConfirm, handleOnAdd, handleOnDelete, handleOnSort } =
    useImageCarousel({ form });
  const { animations, hoverAnimations, buttons } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  return (
    <Field shouldUpdate>
      {() =>
        imageCarouselRender(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          animations,
          buttons,
          hoverAnimations,
          form,
          ...extra,
        })
      }
    </Field>
  );
};
